import React, { useState, useEffect } from 'react';
import { FluentProvider, teamsLightTheme, makeStyles, Spinner } from '@fluentui/react-components';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication, Configuration } from '@azure/msal-browser';
import { MSALAuthProvider } from './contexts/MSALAuthContext';
import { RobustAuthProvider } from './contexts/RobustAuthContext';
import { UnifiedAppProvider } from './contexts/UnifiedAppContext';
import { ErrorProvider } from './contexts/ErrorContext';
import { NavigationGuardProvider } from './contexts/NavigationGuardContext';
import { DisclaimerEnrollmentSummaryProvider } from './contexts/DisclaimerEnrollmentSummaryContext';
import { MSALProtectedRoute } from './components/Auth/MSALProtectedRoute';
import { useConfig } from './components/ConfigProvider';
import { UnifiedLayout } from './components/Layout/UnifiedLayout';
import { Dashboard } from './pages/Dashboard';
import { Policies } from './pages/Policies';
import { PolicyDetails } from './pages/PolicyDetails';
import { EditPolicy } from './pages/EditPolicy';
import { Domains } from './pages/Domains';
import { DomainFeedConfig } from './pages/DomainFeedConfig';
import { Approvals } from './pages/Approvals';
import { Logs } from './pages/Logs';
import { Settings } from './pages/Settings';
import { PolicyCreation } from './pages/PolicyCreation';
import { SystemDebug } from './pages/SystemDebug';
import { DebugAuth } from './pages/DebugAuth';
import { DriftMonitoringSettings } from './pages/DriftMonitoringSettings';
import { DomainFeedSettings } from './pages/DomainFeedSettings';
import { SystemStatus } from './pages/SystemStatus';
import { UserGuide } from './pages/UserGuide';
import { GeneralConfig } from './pages/GeneralConfig';
import { EmailSettings } from './pages/EmailSettings';
import TeamsSyncSettings from './pages/TeamsSyncSettings';
import { SimpleDriftPage } from './pages/SimpleDriftPage';
import { SecurityGroups } from './pages/SecurityGroups';
import { TenantConfiguration } from './pages/TenantConfiguration';
import { CustomDisclaimers } from './pages/CustomDisclaimers';
import { CreateDisclaimer } from './pages/CreateDisclaimer';
import { EditDisclaimer } from './pages/EditDisclaimer';
import { DomainApprovalBanner } from './components/DomainApprovalBanner';
import { OnboardingFlow } from './pages/OnboardingFlow/OnboardingFlow';
import { OnboardingGuard } from './components/Auth/OnboardingGuard';
import { OnboardingSuccessPage } from './pages/CustomDisclaimersOnboarding/OnboardingSuccessPage';
import { OnboardingFailedPage } from './pages/CustomDisclaimersOnboarding/OnboardingFailedPage';
import UsageTracking from './tracking';

const useStyles = makeStyles({
  app: {
    height: '100vh',
    width: '100vw',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
});

// MSAL instance will be created dynamically from runtime config
// No longer created at module level to support runtime configuration

// Inner component that uses styles after FluentProvider is available
const AppContent: React.FC = () => {
  const styles = useStyles();

  return (
    <div className={styles.app} data-testid="app-app">
      <RobustAuthProvider>
        <ErrorProvider>
          <UnifiedAppProvider>
          <Router>
          <Routes>
            {/* Debug route outside of protected routes - only in development/test */}
            {(process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') && (
              <Route path="/debug-auth" element={<DebugAuth />} />
            )}

            {/* Reached via the MSFT consent redirect by end users who have no ADM access */}
            <Route path="/custom-disclaimers/onboarding/success" element={<OnboardingSuccessPage />} />
            <Route path="/custom-disclaimers/onboarding/failure" element={<OnboardingFailedPage />} />

            {/* All other routes require authentication */}
            <Route path="/*" element={
              <MSALProtectedRoute>
                <OnboardingGuard>
                  <DisclaimerEnrollmentSummaryProvider>
                  <NavigationGuardProvider>
                  <DomainApprovalBanner />
                  <UnifiedLayout>
                      <Routes>
                        <Route path="/" element={<Policies />} />
                        <Route path="/onboarding" element={<OnboardingFlow />} />
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/policies" element={<Policies />} />
                        <Route path="/policies/:id" element={<PolicyDetails />} />
                        <Route path="/policies/:id/edit" element={<EditPolicy />} />
                        <Route path="/domains" element={<Domains />} />
                        <Route path="/domain-feed-config" element={<DomainFeedConfig />} />
                        <Route path="/approvals" element={<Approvals />} />
                        <Route path="/logs" element={<Logs />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/tenant-config" element={<TenantConfiguration />} />
                        <Route path="/wizard" element={<PolicyCreation />} />
                        <Route path="/create-policy" element={<PolicyCreation />} />
                        <Route path="/debug" element={<SystemDebug />} />
                        <Route path="/drift-settings" element={<DriftMonitoringSettings />} />
                        <Route path="/domain-feed-settings" element={<DomainFeedSettings />} />
                        <Route path="/system-status" element={<SystemStatus />} />
                        <Route path="/user-guide" element={<UserGuide />} />
                        <Route path="/general-config" element={<GeneralConfig />} />
                        <Route path="/email-settings" element={<EmailSettings />} />
                        <Route path="/teams-sync-settings" element={<TeamsSyncSettings />} />
                        <Route path="/security-groups" element={<SecurityGroups />} />
                        <Route path="/custom-disclaimers" element={<CustomDisclaimers />} />
                        <Route path="/custom-disclaimers/new" element={<CreateDisclaimer />} />
                        <Route path="/custom-disclaimers/:id" element={<EditDisclaimer />} />
                        <Route path="/drift-test" element={<SimpleDriftPage />} />
                      </Routes>
                    </UnifiedLayout>
                  </NavigationGuardProvider>
                  </DisclaimerEnrollmentSummaryProvider>
                  </OnboardingGuard>
                </MSALProtectedRoute>
              } />
            </Routes>
          </Router>
            </UnifiedAppProvider>
          </ErrorProvider>
        </RobustAuthProvider>
    </div>
  );
};

function App() {
  const { config, isLoading } = useConfig();
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!config || isLoading) {
      console.log('⏳ App: Waiting for config...', { hasConfig: !!config, isLoading });
      return;
    }

    // Prevent recreating MSAL if already created for the same config
    if (msalInstance) {
      console.log('✅ App: MSAL instance already exists, skipping recreation');
      return;
    }

    console.log('🔧 App: Creating MSAL instance with config...', {
      clientId: config.azureClientId,
      tenantId: config.azureTenantId,
    });

    // Create MSAL configuration from runtime config
    const msalConfig: Configuration = {
      auth: {
        clientId: config.azureClientId,
        authority: `https://login.microsoftonline.com/${config.azureTenantId}`,
        redirectUri: config.redirectUri,
        postLogoutRedirectUri: config.redirectUri,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
      },
    };

    console.log('🔧 Creating MSAL instance with runtime config', {
      clientId: config.azureClientId,
      authority: `https://login.microsoftonline.com/${config.azureTenantId}`,
      redirectUri: config.redirectUri,
    });

    // Create and initialize MSAL instance
    const instance = new PublicClientApplication(msalConfig);

    instance
      .initialize()
      .then(() => {
        console.log('✅ MSAL instance fully initialized');
        setMsalInstance(instance);
        setIsReady(true);

        initPITracking();
      })
      .catch((error) => {
        console.error('❌ MSAL initialization failed:', error);

        // If initialization fails due to cache issues, try clearing cache
        if (error?.message?.includes('cache')) {
          console.log('Attempting to clear MSAL cache due to error...');
          try {
            if (typeof window !== 'undefined' && window.localStorage) {
              const keysToRemove: string[] = [];
              for (let i = 0; i < window.localStorage.length; i++) {
                const key = window.localStorage.key(i);
                if (key && key.includes('msal')) {
                  keysToRemove.push(key);
                }
              }
              keysToRemove.forEach(key => {
                window.localStorage.removeItem(key);
              });
              console.log(`Cleared ${keysToRemove.length} MSAL cache entries`);

              // Try initializing again after clearing cache
              const retryInstance = new PublicClientApplication(msalConfig);
              retryInstance
                .initialize()
                .then(() => {
                  console.log('✅ MSAL initialized after cache clear');
                  setMsalInstance(retryInstance);
                  setIsReady(true);
                })
                .catch(() => {
                  console.error('Failed to initialize even after cache clear');
                  setIsReady(true);
                });
              return;
            }
          } catch (e) {
            console.warn('Could not clear MSAL cache:', e);
          }
        }

        // Continue anyway - some features might work
        setIsReady(true);
      });
  }, [config, isLoading]);

  // Initialize product insight tracking
  const initPITracking = async () => {
    console.log('Initializing product insight tracking');
    try {
      await UsageTracking.Instance.initialize();
    } catch (error) {
      console.log('Error initializing product insight tracking', error);
    }
  };

  if (!config || isLoading || !isReady || !msalInstance) {
    return (
      <FluentProvider theme={teamsLightTheme}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }} data-testid="app-initializing-screen">
          <Spinner size="large" label="Initializing application..." />
        </div>
      </FluentProvider>
    );
  }

  return (
    <MsalProvider instance={msalInstance}>
      <FluentProvider theme={teamsLightTheme}>
        <MSALAuthProvider>
          <AppContent />
        </MSALAuthProvider>
      </FluentProvider>
    </MsalProvider>
  );
}

export default App;
